import { AxiosError } from "axios";
import { ErrorResponse } from "@/utils/uno-axios";
import { isErrorResponse } from "@/utils/use-fetcher";

// axios-client(react-query 경로)는 uno-axios와 달리 응답 인터셉터에서 error.response.data를
// 벗기지 않아 raw AxiosError가 그대로 전파된다. 그래서 useAppToasts의 error()에 그냥 넘기면
// isErrorResponse 가드를 통과하지 못해 서버가 준 문구가 있어도 늘 message.error.unknown으로 떨어진다.
// 여기서 토스트가 이해하는 형태(ErrorResponse)로 맞춰준다.

// 응답 자체가 없으면(네트워크 단절·타임아웃) undefined.
export function getHttpStatus(error: unknown): number | undefined {
  return (error as AxiosError | undefined)?.response?.status;
}

export function toErrorResponse(error: unknown): ErrorResponse {
  // uno-axios 경로는 이미 벗겨진 상태로 온다.
  if (isErrorResponse(error)) {
    return error;
  }

  const response = (error as AxiosError | undefined)?.response;

  // 서버가 규격 바디를 주면 그대로 쓴다 — 문구·details(다국어 메시지)를 잃지 않는다.
  if (isErrorResponse(response?.data)) {
    return response!.data as ErrorResponse;
  }

  // 규격 바디가 아니면 상태 코드만 살려 합성한다.
  // message를 빈 문자열로 두는 게 핵심이다 — 그래야 토스트가 message.error.http.{code} →
  // message.error.unknown 폴백으로 넘어간다.
  if (response) {
    return {
      error: {
        code: response.status,
        status: response.statusText || "UNKNOWN",
        message: "",
      },
    };
  }

  return {
    error: {
      code: 0,
      status: "NETWORK_ERROR",
      message: "",
    },
  };
}

// GET 재시도 대상인지 — 4xx는 다시 물어봐도 답이 같으므로 재시도하지 않는다.
// 401은 일부러 뺐다. axios-client 인터셉터가 originalRequest._retry로 토큰 재발급을 1회로 묶는데,
// react-query의 retry는 매번 새 config로 요청을 만들어 그 가드를 무력화한다.
export function isRetryableApiError(error: unknown): boolean {
  const status = getHttpStatus(error);
  if (status === undefined) {
    return true;
  }
  return status === 408 || status === 429 || status >= 500;
}
